import { TourStop } from './tour-stop';
import { TourStopKind } from './tour-stop-kind';
import { TourStopPosition } from './tour-stop-position';
import { TourStopButtonAction } from './tour-stop-button-action';
import { projectDisplayText } from '../utils/project-display-text';
import { addTourStopEventListeners } from '../public-api';
import { DriveStep, PopoverDOM } from 'driver.js';

/**
 * A billboard-style tour stop with custom template content.
 *
 * TourStopBillboard displays a large, modal-style popover in the center of the screen
 * without highlighting any specific element. It's ideal for welcome screens,
 * introductory messages, or any content that needs full user attention.
 *
 * The billboard uses a custom HTML template with text interpolation and supports
 * multiple interactive buttons with custom actions.
 *
 * @example
 * ```typescript
 * const template = `
 *   <div class="welcome-screen">
 *     <h1>{{title}}</h1>
 *     <p>{{description}}</p>
 *     <button data-tour-action-key="startButton"></button>
 *     <button data-tour-action-key="skipButton"></button>
 *   </div>
 * `;
 *
 * const billboard = new TourStopBillboard(
 *   template,
 *   {
 *     title: 'Welcome to Our App',
 *     description: 'Let us show you around',
 *     startText: 'Get Started',
 *     skipText: 'Skip Tour'
 *   },
 *   {
 *     startButton: {
 *       displayText: 'Get Started',
 *       listener: () => tourService.nextStop()
 *     },
 *     skipButton: {
 *       displayText: 'Skip Tour',
 *       listener: () => tourService.endTour()
 *     }
 *   }
 * );
 * ```
 */
export class TourStopBillboard extends TourStop {
  /** @inheritdoc */
  override kind: TourStopKind = 'billboard';

  /**
   * Creates a new billboard tour stop.
   *
   * @param template - HTML template string with interpolation placeholders ({{key}})
   * @param displayText - Key-value pairs for text interpolation in the template
   * @param buttonActions - Configuration for interactive buttons in the billboard
   * @param position - Optional positioning configuration for the popover
   *
   * @example
   * ```typescript
   * const welcomeTemplate = `
   *   <div class="text-center p-4">
   *     <h2>{{welcomeTitle}}</h2>
   *     <p>{{welcomeMessage}}</p>
   *     <div class="mt-4">
   *       <button class="btn btn-primary" data-tour-action-key="continueButton">/button>
   *       <button class="btn btn-secondary" data-tour-action-key="skipButton"></button>
   *     </div>
   *   </div>
   * `;
   *
   * const billboard = new TourStopBillboard(
   *   welcomeTemplate,
   *   {
   *     welcomeTitle: 'Welcome to the Tour',
   *     welcomeMessage: 'This tour will guide you through the main features.'
   *   },
   *   {
   *     continueButton: {
   *       displayText: 'Continue',
   *       listener: () => tourService.nextStop()
   *     },
   *     skipButton: {
   *       displayText: 'Skip',
   *       listener: () => tourService.endTour()
   *     }
   *   }
   * );
   * ```
   */
  constructor(
    public template: string,
    public override displayText: Record<string, string>,
    public override buttonActions: Record<string, TourStopButtonAction>,
    position?: TourStopPosition
  ) {
    super();

    // Set positioning if provided
    if (position) {
      this.position = position;
    }
  }

  /**
   * Converts this billboard stop to a driver.js step configuration.
   *
   * This method creates a driver.js step that will display a large popover
   * in the center of the screen with the interpolated template content.
   * The popover will have the 'tour-stop-lg' class for large styling.
   *
   * @returns A driver.js step configuration object
   *
   * @example
   * ```typescript
   * const driverStep = billboard.toDriverJsStep();
   * // Results in a driver.js step with:
   * // - no target element (displays in center of screen)
   * // - popover with interpolated template content
   * // - large styling class applied
   * // - event listeners for interactive buttons
   * ```
   */
  toDriverJsStep() {
    // Generate the popover content by interpolating the template
    const description = projectDisplayText(this.template, this.displayText);

    // Set up event listeners for interactive buttons
    const onPopoverRender = ({ wrapper }: PopoverDOM) => {
      addTourStopEventListeners(this.buttonActions, wrapper);
    };

    return {
      popover: {
        description,
        side: this.position?.side,
        align: this.position?.align,
        showButtons: [], // Hide default driver.js buttons since we use custom ones
        popoverClass: 'tour-stop tour-stop-lg', // Apply large styling for billboard
        onPopoverRender,
      },
    } as DriveStep;
  }
}
